/**
 * ReportAccessAuditPage — `/reporting/access-audit`.
 *
 * Wired to GET /v1/reporting/access-audit on service-reporting-api. The
 * endpoint surfaces who-read-which-report by deriving rows from the
 * report_run table (every row carries the actor in requestedBy).
 */
import { type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { listAccessAudit, type AccessAuditRowWire } from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export function ReportAccessAuditPage(): JSX.Element {
  const audit = useQuery<readonly AccessAuditRowWire[], Error>({
    queryKey: ['reporting', 'access-audit'],
    queryFn: () => listAccessAudit(50),
    retry: false,
  });

  const denied =
    audit.isError && audit.error instanceof ApiError &&
    (audit.error.status === 401 || audit.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Report access audit"
        description="Who read which report."
      />

      {denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/readAccessAudit</code> required.
        </DeferredNotice>
      ) : audit.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {audit.error.message}
        </DeferredNotice>
      ) : (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <ScrollText className="w-4 h-4 text-nipost-green" />
            <h3 className="font-semibold text-gray-900">
              {audit.isPending ? 'Loading…' : `${(audit.data?.length ?? 0).toLocaleString('en-US')} access events`}
            </h3>
          </div>
          {audit.isPending ? (
            <p className="text-sm text-muted">Loading access events…</p>
          ) : (audit.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted py-4 text-center">No access events recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="access-audit-table">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">When</th>
                    <th className="text-left px-4 py-2">Actor</th>
                    <th className="text-left px-4 py-2">Action</th>
                    <th className="text-left px-4 py-2">Entity</th>
                    <th className="text-left px-4 py-2">Report key</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(audit.data ?? []).map((row) => (
                    <tr key={row.id} data-testid="access-audit-row">
                      <td className="px-4 py-2 text-xs font-mono">{formatDateTime(row.when)}</td>
                      <td className="px-4 py-2 text-xs">{row.actor}</td>
                      <td className="px-4 py-2 text-xs"><code className="font-mono">{row.action}</code></td>
                      <td className="px-4 py-2 text-xs"><code className="font-mono">{row.entityType}/{row.entityId.slice(0, 8)}…</code></td>
                      <td className="px-4 py-2 text-xs"><code className="font-mono">{row.reportKey ?? '—'}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
